// @core/stores/chat/ChatMessageStore.ts
// Auto-generated by corebuilder. DO NOT EDIT.

import { makeAutoObservable, runInAction } from 'mobx';
import { chatApi } from '@core/api';
import type {
  AddReactionRequest,
  EditMessageRequest,
  ForwardMessagesRequest,
  ForwardMessagesResponse,
  ListMessageReadersResponse,
  ListPinnedMessagesResponse,
  Message,
  MessageResponse,
  PinMessageRequest,
  ReplyInThreadRequest,
  SearchMessagesResponse,
  SendMessageRequest,
} from '@core/types/chat';
import type { PaginatedRequest, PageInfo } from '@core/types/pagination';

class ChatMessageStore {
  // List state
  items: Message[] = [];
  pageInfo: PageInfo | null = null;

  // Single item state
  currentItem: Message | null = null;

  // Loading states
  isLoading = false;
  isLoadingItem = false;
  isSaving = false;
  isDeleting = false;

  // Error state (stores full ApiClientError for request context)
  error: unknown = null;
  // Cache by ID
  private cache = new Map<string, Message>();

  constructor() {
    makeAutoObservable(this);
  }
  // Computed
  get hasMore(): boolean {
    return this.pageInfo?.has_next ?? false;
  }

  get isEmpty(): boolean {
    return this.items.length === 0 && !this.isLoading;
  }

  // Cache helpers
  getById(id: string): Message | undefined {
    return this.cache.get(id);
  }
  private updateCache(item: Message | undefined): void {
    if (item?.entity?.id) {
      this.cache.set(item.entity.id, item);
    }
  }
  private removeFromCache(id: string): void {
    this.cache.delete(id);
  }

  // Actions
  setError(error: unknown): void {
    this.error = error;
  }

  clearError(): void {
    this.error = null;
  }
  clearCurrent(): void {
    this.currentItem = null;
  }

  reset(): void {
    this.items = [];
    this.pageInfo = null;
    this.currentItem = null;
    this.error = null;
    this.cache.clear();
  }

  /**
   * Add reaction to message
   */
  async AddReaction(conversation_id: string, message_id: string, data: AddReactionRequest): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await chatApi.AddReaction(conversation_id, message_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Delete a message
   */
  async DeleteMessage(conversation_id: string, message_id: string): Promise<void> {
    this.isDeleting = true;
    this.error = null;

    try {
      await chatApi.DeleteMessage(conversation_id, message_id);

      runInAction(() => {
        // Remove from list
        this.items = this.items.filter(i => i.entity?.id !== message_id);

        // Clear current item if same
        if (this.currentItem?.entity?.id === message_id) {
          this.currentItem = null;
        }

        this.removeFromCache(message_id);
        this.isDeleting = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isDeleting = false;
      });
      throw error;
    }
  }

  /**
   * Edit a message
   */
  async EditMessage(conversation_id: string, message_id: string, data: EditMessageRequest): Promise<MessageResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await chatApi.EditMessage(conversation_id, message_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Forward messages
   */
  async ForwardMessages(conversation_id: string, data: ForwardMessagesRequest): Promise<ForwardMessagesResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await chatApi.ForwardMessages(conversation_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get message by ID
   */
  async GetMessage(conversation_id: string, message_id: string): Promise<Message> {
    // Check cache first
    const cached = this.cache.get(message_id);
    if (cached) {
      this.currentItem = cached;
      return cached;
    }

    this.isLoadingItem = true;
    this.error = null;

    try {
      const response = await chatApi.GetMessage(conversation_id, message_id);
      const item = response.message;

      runInAction(() => {
        this.currentItem = item ?? null;
        this.updateCache(item);
        this.isLoadingItem = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoadingItem = false;
      });
      throw error;
    }
  }

  /**
   * List message readers
   */
  async ListMessageReaders(conversation_id: string, message_id: string, params?: PaginatedRequest): Promise<ListMessageReadersResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await chatApi.ListMessageReaders(conversation_id, message_id, params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List messages in conversation
   */
  async ListMessages(conversation_id: string, params?: PaginatedRequest): Promise<void> {
    this.isLoading = true;
    this.error = null;

    try {
      const response = await chatApi.ListMessages(conversation_id, params);

      runInAction(() => {
        this.items = response.messages?.messages ?? [];
        this.pageInfo = response.pagination?.page_info ?? null;
        this.isLoading = false;

        // Update cache
        this.items.forEach(item => this.updateCache(item));
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List pinned messages
   */
  async ListPinnedMessages(conversation_id: string, params?: PaginatedRequest): Promise<ListPinnedMessagesResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await chatApi.ListPinnedMessages(conversation_id, params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List thread replies
   */
  async ListThreadReplies(conversation_id: string, message_id: string, params?: PaginatedRequest): Promise<void> {
    this.isLoading = true;
    this.error = null;

    try {
      const response = await chatApi.ListThreadReplies(conversation_id, message_id, params);

      runInAction(() => {
        this.items = response.messages?.messages ?? [];
        this.pageInfo = response.pagination?.page_info ?? null;
        this.isLoading = false;

        // Update cache
        this.items.forEach(item => this.updateCache(item));
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Pin a message
   */
  async PinMessage(conversation_id: string, message_id: string, data: PinMessageRequest): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await chatApi.PinMessage(conversation_id, message_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Remove reaction from message
   */
  async RemoveReaction(conversation_id: string, message_id: string, emoji: string): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await chatApi.RemoveReaction(conversation_id, message_id, emoji);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Reply in thread
   */
  async ReplyInThread(conversation_id: string, message_id: string, data: ReplyInThreadRequest): Promise<MessageResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await chatApi.ReplyInThread(conversation_id, message_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Search messages
   */
  async SearchMessages(params?: { limit?: string | number | boolean; cursor?: string | number | boolean; direction?: string | number | boolean; sort?: string | number | boolean; }): Promise<SearchMessagesResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await chatApi.SearchMessages(params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Send a message
   */
  async SendMessage(conversation_id: string, data: SendMessageRequest): Promise<MessageResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await chatApi.SendMessage(conversation_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Unpin a message
   */
  async UnpinMessage(conversation_id: string, message_id: string): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await chatApi.UnpinMessage(conversation_id, message_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }
}

export const chatMessageStore = new ChatMessageStore();
